import { useMemo, useState } from "react";
import { Globe, Search, X } from "lucide-react";

const chip = (on) => `px-3 h-8 rounded-full text-xs font-bold border transition-colors ${on ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-[#27272A] text-neutral-400 hover:border-neutral-500"}`;
const REGION_NAMES = { AF: "Africa", EU: "Europe", AS: "Asia", NA: "North America", SA: "South America", OC: "Oceania" };

/** Pick any mix of regions ("world", "africa", …) and countries (ISO-2). regions/countries come from the API. */
export function MarketPicker({ regions = [], countries = [], value = [], onChange, testPrefix = "market" }) {
  const [q, setQ] = useState("");
  const byCode = useMemo(() => Object.fromEntries([...regions, ...countries].map((c) => [c.code, c])), [regions, countries]);
  const toggle = (code) => onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return countries.filter((c) => c.label.toLowerCase().includes(t) || c.code === t).slice(0, 12);
  }, [q, countries]);
  const popular = countries.filter((c) => c.popular);

  return (
    <div className="space-y-2" data-testid={`${testPrefix}-picker`}>
      <div className="flex flex-wrap items-center gap-2">
        <Globe className="h-4 w-4 text-neutral-500" />
        {regions.map((r) => <button key={r.code} onClick={() => toggle(r.code)} className={chip(value.includes(r.code))} data-testid={`${testPrefix}-${r.code}`}>{r.label}</button>)}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-3.5 w-3.5 text-neutral-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any country…" data-testid={`${testPrefix}-search`}
            className="h-8 w-56 rounded-md bg-[#0A0A0A] border border-[#27272A] pl-8 pr-2 text-xs text-white focus:outline-none focus:border-[#FF3B30]" />
          {matches.length > 0 && (
            <div className="absolute z-20 mt-1 w-64 max-h-64 overflow-auto rounded-md border border-[#27272A] bg-[#0E0E10] shadow-xl" data-testid={`${testPrefix}-results`}>
              {matches.map((c) => (
                <button key={c.code} onClick={() => { toggle(c.code); setQ(""); }} data-testid={`${testPrefix}-result-${c.code}`}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex justify-between ${value.includes(c.code) ? "text-[#FF3B30]" : "text-neutral-200"}`}>
                  <span>{c.label}</span><span className="text-neutral-600">{REGION_NAMES[c.region] || ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {popular.filter((c) => !value.includes(c.code)).slice(0, 10).map((c) => (
          <button key={c.code} onClick={() => toggle(c.code)} className="px-2.5 h-7 rounded-full text-[11px] border border-[#27272A] text-neutral-500 hover:text-white hover:border-neutral-500" data-testid={`${testPrefix}-popular-${c.code}`}>{c.label}</button>
        ))}
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testPrefix}-selected`}>
          <span className="text-[11px] text-neutral-500 mr-1">Scanning:</span>
          {value.map((code) => (
            <span key={code} className="inline-flex items-center gap-1 pl-2.5 pr-1 h-7 rounded-full bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-xs text-white" data-testid={`${testPrefix}-selected-${code}`}>
              {byCode[code]?.label || code}
              <button onClick={() => toggle(code)} className="h-5 w-5 inline-flex items-center justify-center rounded-full hover:bg-white/10" aria-label={`Remove ${byCode[code]?.label || code}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
